import { useState } from "react";
import { type SaleSummary, formatMoney } from "./api";
import { Box, Table, Th } from "./ui";

/**
 * The sales a day at a time, newest first (D, 9 October 2026: "page 1 is yesterday, page 2
 * is the day before"), and inside each day every item sold, line by line, with what it
 * brought in, what it cost and what it made, by location, and the day's totals.
 *
 * Paid sales only: a void or a correction was never money. An item's revenue is what its
 * lines came to; a discount given on the whole sale is shown on a line of its own, so the
 * day adds up to what the customers paid. Cost is the same rule as the reports.
 */
export function DailySummary({ sales }: { readonly sales: readonly SaleSummary[] }): React.ReactElement | null {
  const paid = sales.filter((sale) => sale.status === "paid");
  const days = [...new Set(paid.map((sale) => sale.saleDate))].sort((a, b) => b.localeCompare(a));
  const [page, setPage] = useState(0);
  if (days.length === 0) return null;
  const at = Math.min(page, days.length - 1);
  const day = days[at]!;
  const ofDay = paid.filter((sale) => sale.saleDate === day);

  type Item = { place: string; name: string; quantity: number; revenue: number; cost: number; noCost: boolean };
  const items = new Map<string, Item>();
  let adjustment = 0;
  for (const sale of ofDay) {
    const place = sale.location ?? sale.event ?? "No location set";
    const lineSum = sale.lines.reduce((sum, line) => sum + line.lineTotalCents, 0);
    adjustment += sale.totalCents - lineSum;
    for (const line of sale.lines) {
      const key = `${place}|${line.description}`;
      const item = items.get(key) ?? { place, name: line.description, quantity: 0, revenue: 0, cost: 0, noCost: false };
      const cost = line.lineId ? sale.lineCosts?.[line.lineId] : undefined;
      item.quantity += line.quantity;
      item.revenue += line.lineTotalCents;
      item.cost += cost ?? 0;
      // Sold for money with nothing against it: the profit on it is not real yet.
      if (line.lineTotalCents > 0 && (sale.costMissing ?? []).includes(line.description)) item.noCost = true;
      items.set(key, item);
    }
  }
  const rows = [...items.values()].sort((a, b) => a.place.localeCompare(b.place) || b.revenue - a.revenue);
  const revenue = ofDay.reduce((sum, sale) => sum + sale.totalCents, 0);
  const cost = rows.reduce((sum, row) => sum + row.cost, 0);
  const readable = new Date(`${day}T12:00:00+08:00`).toLocaleDateString("en-SG", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Singapore",
  });
  const profitClass = (value: number): string => (value < 0 ? "text-danger" : "text-success");
  const pageButton = "ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0";

  return (
    <Box
      title={`Daily summary - ${readable}`}
      actions={
        <div className="flex items-center gap-2">
          <button type="button" className={pageButton} disabled={at >= days.length - 1} onClick={() => setPage(at + 1)}>
            <i className="ri-arrow-left-s-line" /> Day before
          </button>
          <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
            {at + 1} of {days.length}
          </span>
          <button type="button" className={pageButton} disabled={at === 0} onClick={() => setPage(at - 1)}>
            Day after <i className="ri-arrow-right-s-line" />
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-4 text-center mb-4">
        <div>
          <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Revenue</p>
          <p className="font-semibold text-[1.25rem] mb-0">{formatMoney(revenue)}</p>
        </div>
        <div>
          <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Cost</p>
          <p className="font-semibold text-[1.25rem] mb-0">{formatMoney(cost)}</p>
        </div>
        <div>
          <p className="text-[#8c9097] dark:text-white/50 text-[0.75rem] mb-1">Profit</p>
          <p className={`font-semibold text-[1.25rem] mb-0 ${profitClass(revenue - cost)}`}>{formatMoney(revenue - cost)}</p>
        </div>
      </div>
      <Table
        head={
          <>
            <Th>Location</Th>
            <Th>Item</Th>
            <Th end>Quantity</Th>
            <Th end>Revenue</Th>
            <Th end>Cost</Th>
            <Th end>Profit</Th>
          </>
        }
      >
        {rows.map((row) => (
          <tr key={`${row.place}|${row.name}`} className="border-b border-defaultborder">
            <td className="text-[#8c9097] dark:text-white/50">{row.place}</td>
            <td className="!whitespace-normal min-w-[14rem]">
              {row.name}
              {row.noCost ? <div className="text-danger text-[0.75rem]">No cost price: put it in on Inventory</div> : null}
            </td>
            <td className="!text-end">{row.quantity}</td>
            <td className="!text-end">{formatMoney(row.revenue)}</td>
            <td className={`!text-end ${row.noCost ? "text-danger" : "text-[#8c9097] dark:text-white/50"}`}>{formatMoney(row.cost)}</td>
            <td className={`!text-end font-semibold ${profitClass(row.revenue - row.cost)}`}>{formatMoney(row.revenue - row.cost)}</td>
          </tr>
        ))}
        {adjustment !== 0 ? (
          <tr className="border-b border-defaultborder">
            <td />
            <td className="text-[#8c9097] dark:text-white/50">{adjustment < 0 ? "Discounts given" : "Added on top"}</td>
            <td />
            <td className="!text-end">{formatMoney(adjustment)}</td>
            <td />
            <td className={`!text-end font-semibold ${profitClass(adjustment)}`}>{formatMoney(adjustment)}</td>
          </tr>
        ) : null}
        <tr className="bg-light">
          <td colSpan={3} className="font-semibold">
            Total for the day, {ofDay.length} {ofDay.length === 1 ? "sale" : "sales"}
          </td>
          <td className="!text-end font-semibold">{formatMoney(revenue)}</td>
          <td className="!text-end font-semibold">{formatMoney(cost)}</td>
          <td className={`!text-end font-semibold ${profitClass(revenue - cost)}`}>{formatMoney(revenue - cost)}</td>
        </tr>
      </Table>
      <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
        Paid sales. Cost is what the stock came in at, or the product's cost price for what is not counted. An item in red has
        no cost price, or a cost of 0, so it shows as all profit until its cost price is put in on Inventory.
        {sales.length >= 300 ? " The latest 300 sales; pick a date above for an older day." : ""}
      </p>
    </Box>
  );
}
